import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Box, Grid, Typography } from '@material-ui/core';
import SentimentDissatisfiedRoundedIcon from '@material-ui/icons/SentimentDissatisfiedRounded';
import { globalGetService, globalPostService } from '../../../../globalServices';
import { checkApiStatus } from '../../../utils_v2';
import { Pagination, PageLoader } from '../../../shared_elements';
import { getNotificationBarDataAc } from '../../../../shared/actionCreators';
import NotificationHeader from '../components/NotificationHeader';
import NotificationTabs from '../components/NotificationTabs';
import NotificationToolbar from '../components/NotificationToolbar';
import NotificationCard from '../components/NotificationCard';
import NotificationSummary from '../components/NotificationSummary';
import RecentActivityCard from '../components/RecentActivityCard';
import '../styles/notification.scss';

const getTypeKey = (item) => {
    const label = (item?.notification_type?.label || '').trim().toLowerCase();
    if (label.includes('release')) return 'release';
    if (label.includes('announcement')) return 'announcement';
    if (label.includes('system') || label.includes('alert')) return 'system_alert';
    return 'other';
};

const getReadStatus = (item) => {
    if (item?.status === 1) return { key: 'read', label: 'Read' };
    return getTypeKey(item) === 'system_alert'
        ? { key: 'alert', label: 'Alert' }
        : { key: 'unread', label: 'Unread' };
};

const stripHtml = (html = '') => {
    const container = document.createElement('div');
    container.innerHTML = html;
    return container.textContent || '';
};

const formatNotificationDate = (dateString) => {
    const date = moment(dateString);
    if (!date.isValid()) return '--';
    return date.isSame(moment(), 'day') ? `Today, ${date.format('h:mm A')}` : date.format('DD MMM YYYY, HH:mm');
};

const Notification = ({ dispatch }) => {
    const [notificationInfo, setNotificationInfo] = useState({ notifications: [] });
    const [isLoading, setLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [activeTab, setActiveTab] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    const getNotificationList = useCallback(async (query = {}) => {
        const request = { page: 1, per_page: 20, ...query };
        setLoading(true);
        const response = await globalGetService('notifications/', request);
        setLoading(false);
        if (checkApiStatus(response)) {
            setNotificationInfo(response.data.data);
        }
    }, []);

    useEffect(() => {
        getNotificationList({ per_page: 20 });
    }, [getNotificationList]);

    const notifications = useMemo(() => notificationInfo?.notifications || [], [notificationInfo?.notifications]);
    const hasPagination = Boolean(notificationInfo?.pagination?.total > 0);
    const hasActiveFilters = activeTab !== 'all' || Boolean(searchValue) || typeFilter !== 'all' || statusFilter !== 'all';

    const refreshAfterMarkRead = () => {
        dispatch(getNotificationBarDataAc());
        getNotificationList({ page: 1, per_page: notificationInfo?.pagination?.per_page || 20 });
    };

    const handleMarkAllRead = () => {
        globalPostService('/notifications/edit/', { mark_all_read: true }).then(response => {
            if (checkApiStatus(response)) refreshAfterMarkRead();
        });
    };

    const handleMarkRead = (selectedItem) => {
        globalPostService('/notifications/edit/', { notification_id: selectedItem?.notification_id, status: 1 }).then(response => {
            if (checkApiStatus(response)) refreshAfterMarkRead();
        });
    };

    const derived = useMemo(() => {
        const unread = notifications.filter(item => getReadStatus(item).key !== 'read');
        const byType = (key) => notifications.filter(item => getTypeKey(item) === key);
        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        startOfMonth.setHours(0, 0, 0, 0);
        const releasesThisMonth = byType('release').filter(item => new Date(item?.created_at) >= startOfMonth);

        return {
            unreadCount: unread.length,
            announcementCount: byType('announcement').length,
            releaseCount: byType('release').length,
            systemAlertCount: byType('system_alert').length,
            releasesThisMonthCount: releasesThisMonth.length,
            recentActivity: [...notifications]
                .sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at))
                .slice(0, 3)
        };
    }, [notifications]);

    const tabs = [
        { key: 'all', label: 'All', count: notifications.length },
        { key: 'unread', label: 'Unread', count: derived.unreadCount },
        { key: 'announcement', label: 'Announcements', count: derived.announcementCount },
        { key: 'release', label: 'Releases', count: derived.releaseCount },
        { key: 'system_alert', label: 'System Alerts', count: derived.systemAlertCount }
    ];

    const visibleNotifications = useMemo(() => {
        const normalizedSearch = searchValue.trim().toLowerCase();

        const filtered = notifications.filter(item => {
            const typeKey = getTypeKey(item);
            const status = getReadStatus(item);

            const matchesTab = activeTab === 'all'
                || (activeTab === 'unread' ? status.key !== 'read' : typeKey === activeTab);
            const matchesType = typeFilter === 'all' || typeKey === typeFilter;
            const matchesStatus = statusFilter === 'all' || (statusFilter === 'read' ? status.key === 'read' : status.key !== 'read');
            const matchesSearch = !normalizedSearch || `${item?.subject || ''} ${stripHtml(item?.description)}`.toLowerCase().includes(normalizedSearch);

            return matchesTab && matchesType && matchesStatus && matchesSearch;
        });

        return filtered.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at));
    }, [notifications, activeTab, typeFilter, statusFilter, searchValue]);

    const handleClearFilters = () => {
        setSearchValue('');
        setActiveTab('all');
        setTypeFilter('all');
        setStatusFilter('all');
    };

    return (
        <Box className="notification-page">
            <NotificationHeader onMarkAllRead={handleMarkAllRead} unreadCount={derived.unreadCount} />
            <NotificationTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
            <NotificationToolbar
                searchValue={searchValue}
                onSearchChange={setSearchValue}
                typeFilter={typeFilter}
                onTypeFilterChange={setTypeFilter}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                onClearFilters={handleClearFilters}
            />

            <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} lg={9}>
                    <Box className="notification-page-results-header">
                        <Box>
                            <Typography className="notification-page-results-title">Notification feed</Typography>
                            <Typography className="notification-page-results-subtitle">{visibleNotifications.length} {visibleNotifications.length === 1 ? 'notification' : 'notifications'} shown</Typography>
                        </Box>
                        {hasActiveFilters && (
                            <Box component="button" type="button" onClick={handleClearFilters} className="notification-page-results-reset">Reset view</Box>
                        )}
                    </Box>
                    <Box className={`notification-page-list${hasPagination && visibleNotifications.length > 0 ? ' has-pagination' : ''}${visibleNotifications.length === 0 ? ' is-empty' : ''}`}>
                        {visibleNotifications.length > 0 ? (
                            visibleNotifications.map((item, index) => (
                                <NotificationCard
                                    key={item?.notification_id || index}
                                    item={item}
                                    typeKey={getTypeKey(item)}
                                    status={getReadStatus(item)}
                                    description={stripHtml(item?.description)}
                                    formattedDate={formatNotificationDate(item?.created_at)}
                                    onRead={handleMarkRead}
                                />
                            ))
                        ) : (
                            <Box className="notification-page-empty">
                                <Box className="notification-page-empty-icon"><SentimentDissatisfiedRoundedIcon /></Box>
                                <Typography className="notification-page-empty-title">
                                    {searchValue.trim()
                                        ? `No results for ${searchValue.trim()}`
                                        : hasActiveFilters
                                            ? 'No notifications match these filters'
                                            : 'You’re all caught up'}
                                </Typography>
                                <Typography className="notification-page-empty-subtitle">
                                    {searchValue.trim()
                                        ? 'Try a different search term or reset the view to browse all notifications.'
                                        : hasActiveFilters
                                            ? 'Adjust the notification type or status filters, or reset the view to display all notifications.'
                                            : 'There are no new product releases, announcements or system alerts to display.'}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    {hasPagination && visibleNotifications?.length > 0 && (
                        <Box className="notification-page-footer">
                            <Pagination
                                pagination={notificationInfo.pagination}
                                stacks={[10, 20, 50]}
                                onChangePage={(event, newPage) => getNotificationList({
                                    page: newPage + 1,
                                    per_page: notificationInfo.pagination.per_page || 20
                                })}
                                onChangeRowsPerPage={(event) => getNotificationList({
                                    page: 1,
                                    per_page: Number(event.target.value)
                                })}
                            />
                        </Box>
                    )}
                </Grid>

                <Grid item xs={12} lg={3}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <NotificationSummary
                                unreadCount={derived.unreadCount}
                                releasesThisMonthCount={derived.releasesThisMonthCount}
                                announcementCount={derived.announcementCount}
                                systemAlertCount={derived.systemAlertCount}
                            />
                        </Grid>
                        <Grid item xs={12}><RecentActivityCard items={derived.recentActivity} formatDate={formatNotificationDate} /></Grid>
                    </Grid>
                </Grid>
            </Grid>

            {isLoading ? <PageLoader /> : null}
        </Box>
    );
};

export default connect()(Notification);
